import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import Carousel from 'nuka-carousel';
import { stub as $t } from '@nbcnews/analytics-framework';

import Breakpoints from 'lib/Breakpoints';
import { getDateTime } from 'lib/DateTime';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { UNIVERSAL_CHECKOUT_ENABLED } from 'lib/brandFeatures';
import AIMS_FLAVORS from 'lib/aimsFlavors';
import buildAffiliateLink from 'lib/buildAffiliateLink';
import { getDataActivityMapID } from 'lib/articleUtils';
import { trackEcommerceShopClick } from 'lib/trackEcommerceShopClick';

import Link from 'components/Link';
import { Picture } from 'components/Picture';
import ShopButtonInfo from 'components/ShopButtonInfo';
import { Save } from 'components/Social/SocialShareMenu/Save';
import { getAmazonUrlProductAsinAndAssociateTag } from 'lib/commerceUtils';
import { getProductOfferPricesAndAvailability } from 'lib/productOfferPriceHelpers';
import { PRODUCT } from 'lib/myNewsConstants';
import { withViewportVisibility } from 'lib/Hooks/withViewportVisibility';
import { Container } from './Container';
import { PromotionalMedia } from './PromotionalMedia';
import { ProsCons } from './ProsCons';

import './styles.themed.scss';

const block = 'listicleProduct';

const ENCODER_STOP_DATE = 'Wed Apr 15 2020 00:00:00';

/**
 * Functional component that renders a product in a listicle format.
 * @param {object} props
 * @param {string} [props.addClassNames] - Optional additional CSS class names to apply to the component
 * @param {string} props.articleCanonicalUrl - The canonical URL of the article
 * @param {string} props.articlePublishDate - The article publish date.
 * @param {string} [props.hyphenId] - Optional hyphen ID for data attributes
 * @param {object} props.product - The product object containing all product information
 * @param {string} [props.size='medium'] - The size variant of the component
 * @param {string} props.vertical - The vertical/brand identifier for display variance
 * @returns {React.ReactElement} The rendered ListicleProduct component
 */
const ListicleProduct = ({
  addClassNames = '',
  articleCanonicalUrl,
  articlePublishDate,
  hyphenId = '',
  product,
  size = 'medium',
  vertical,
}) => {
  const [currentMedia, setCurrentMedia] = useState(product.teaseImage?.url?.primary);
  const [currentMediaWidth] = useState(product.teaseImage?.width);
  const [currentMediaHeight] = useState(product.teaseImage?.height);
  const [currSlideIdx, setCurrSlideIdx] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  /**
   * Handles breakpoint changes to update mobile state
   */
  const onBreakpointChange = () => {
    setIsMobile(Breakpoints.isS());
  };

  useEffect(() => {
    setIsMobile(Breakpoints.isS());
    const smallMQL = Breakpoints.getSmallMQL();
    smallMQL?.addListener(onBreakpointChange);
    $t('register', 'mbt_ecommerce_shop_button', { allowDuplicate: true });

    return () => {
      smallMQL?.removeListener(onBreakpointChange);
    };
  }, []);

  /**
   * Updates the current media and slide index state
   * @param {string} newCurrentMedia - The URL of the new current media
   * @param {number} newCurrSlideIdx - The index of the new current slide
   */
  const changeCurrentMedia = (newCurrentMedia, newCurrSlideIdx) => {
    setCurrentMedia(newCurrentMedia);
    setCurrSlideIdx(newCurrSlideIdx);
  };

  /**
   * Gets the primary offer from the product's 'offers' array
   * @returns {object|undefined} The first offer object or undefined if no offers exist
   */
  const getPrimaryOffer = () => product?.offers?.[0];

  /**
   * Builds the product link, applying affiliate encoding if applicable
   * @param {string} [externalUrl] - Optional external URL to use instead of primary offer URL
   * @returns {string} The final product URL with affiliate encoding if applicable
   */
  const getProductLink = (externalUrl) => {
    let productLink = externalUrl ?? getPrimaryOffer()?.externalUrl;

    if (!getFeatureConfigForBrand(UNIVERSAL_CHECKOUT_ENABLED, vertical)) {
      const fetchedEncoderStopDate = getDateTime(ENCODER_STOP_DATE);
      const fetchedCurrentArticlePublishDate = getDateTime(articlePublishDate);
      if (fetchedCurrentArticlePublishDate < fetchedEncoderStopDate) {
        productLink = buildAffiliateLink({ url: productLink, canonicalUrl: articleCanonicalUrl });
      }
    }

    return productLink;
  };

  /**
   * Renders the product picture with responsive sizing and affiliate link
   * @param {object} [media] - Optional media object to render instead of default tease image
   * @param {object} media.url - The media URL object
   * @param {string} media.url.primary - The primary URL for the media
   * @param {number} [media.width] - The width of the media
   * @param {number} [media.height] - The height of the media
   * @returns {React.ReactElement} The rendered picture component with link and save button
   */
  const picture = (media) => {
    const {
      id: productId,
      name,
      teaseImage,
    } = product;

    const mediaUrl = media?.url?.primary;
    const mediaWidth = media?.width;
    const mediaHeight = media?.height;

    const isLarge = size === 'large';
    const isOde = isLarge;

    const sizes = isLarge
      ? {
        s: AIMS_FLAVORS.FOCAL_560x560,
      }
      : {
        s: AIMS_FLAVORS.FIT_320w,
        m: AIMS_FLAVORS.FIT_360w,
        l: AIMS_FLAVORS.FIT_260w,
        xl: AIMS_FLAVORS.FIT_260w,
      };
    const flavor = isLarge ? 'focal' : 'fit';

    const width = isLarge ? 560 : mediaWidth || currentMediaWidth;
    const height = isLarge ? 560 : mediaHeight || currentMediaHeight;

    const pictureLinkClasses = classNames(
      `${block}__pictureLink`,
      'listicle-product__picture-link',
      {
        db: !mediaUrl,
        'db-m dn': !isMobile,
        'listicle-product__picture-link--isOde': isOde,
      },
    );

    const productLink = getProductLink();

    return (
      <div className={pictureLinkClasses} data-testid="listicle-product__picture-link-wrapper">
        <Link
          className="w-100"
          data-product-id={productId}
          data-testid="listicle-product-picture-link"
          href={productLink}
          onClick={() => trackEcommerceShopClick({ vertical, product })}
          rel="sponsored noopener noreferrer"
          target="_blank"
        >
          <Picture
            alt={teaseImage?.altText || name}
            flavor={flavor}
            lazyloaded
            originalHeight={height}
            originalWidth={width}
            responsiveFlavors={sizes}
            url={mediaUrl || currentMedia}
          />
        </Link>
        <Save
          contentId={productId}
          contentType={PRODUCT}
          options={{ isThumbnail: true, showCTA: true }}
        />
      </div>
    );
  };

  /**
   * Renders the mobile media carousel slider
   * @returns {React.ReactElement} The mobile media carousel component
   */
  const mobileMediaSlide = () => {
    const { promotionalMedia } = product;

    return (
      <div
        className={classNames('listicle-product__mobile-media', { dn: isMobile })}
        data-test="listicle-media-wrapper"
        data-testid="listicle-media-wrapper"
      >
        <Carousel
          afterSlide={(nextIdx) => setCurrSlideIdx(nextIdx)}
          decorators={[]}
          slideIndex={currSlideIdx}
          withoutControls
        >
          {promotionalMedia.map((image) => (
            image?.url?.primary && (
              <div
                key={image.url.primary}
                className="listicle-product__mobile-media-item"
                data-test="listicle-media-item"
                data-testid="listicle-media-item"
              >
                {picture(image)}
              </div>
            )
          ))}
        </Carousel>
      </div>
    );
  };

  /**
   * Renders the product offers with shop buttons and pricing information
   * @returns {React.ReactElement|null} The offers component or null if no offers exist
   */
  const renderOffers = () => {
    const {
      id: productId,
      name,
      offers,
    } = product;

    if (!Array.isArray(offers) || offers.length === 0) {
      return null;
    }

    return (
      <div className="listicle-product__offers">
        {offers.map((offer) => {
          if (!offer) {
            return null;
          }

          const vendorUrl = getProductLink(offer.externalUrl);

          let seller = offer.seller?.name;

          if (getFeatureConfigForBrand(UNIVERSAL_CHECKOUT_ENABLED, vertical)) {
            seller = 'Comprar';
          }

          const { list, sale } = getProductOfferPricesAndAvailability(offer);

          return (
            <ShopButtonInfo
              key={vendorUrl}
              additionalStyles={{
                shop: 'listicle-product__button',
              }}
              articlePublishDate={articlePublishDate}
              listPrice={list}
              name={name}
              productId={productId}
              productUrl={vendorUrl}
              salePrice={sale}
              seller={seller}
              vertical={vertical}
            />
          );
        })}
      </div>
    );
  };

  /**
   * Renders the universal checkout seller information if enabled
   * @param {Array} offers - Array of offer objects
   * @returns {React.ReactElement|null} The seller name component or null
   */
  const renderUniCheckoutSeller = (offers) => (
    getFeatureConfigForBrand(UNIVERSAL_CHECKOUT_ENABLED, vertical) && offers?.[0]?.seller
      ? (
        <p className="listicle-product__seller-name">
          Vendido por&nbsp;
          {offers[0].seller.name}
        </p>
      )
      : null
  );

  const {
    id: productId,
    description,
    name,
    offers,
    promotionalMedia,
    prosAndCons,
  } = product;

  const productLink = getProductLink();

  const { productAsin, associateTag } = getAmazonUrlProductAsinAndAssociateTag(productLink);

  const { primary: primaryDescription } = description || {};
  const { pros = {}, cons = {} } = prosAndCons || {};

  const isLarge = size === 'large';
  const hasPromotionalMedia = !!promotionalMedia?.length && isLarge;
  const hasProsCons = pros?.items?.length > 0;

  return (
    <div
      id={productId}
      className={addClassNames}
      data-hyphen-id={hyphenId || undefined}
      data-testid="listicle-product"
    >
      <Container isLarge={isLarge} productName={name}>
        <div
          className={
            classNames('listicle-product__border', { 'df flex-column flex-row-m': isLarge, hasProsCons })
          }
        >
          { hasPromotionalMedia && !isMobile && (
            <PromotionalMedia
              additionalStyles={{
                container: 'dn df-m flex-column w-4-m flex-grow-0 flex-shrink-0',
                item: 'mb2',
              }}
              media={promotionalMedia}
              selectedIdx={currSlideIdx}
              selectMedia={changeCurrentMedia}
            />
          )}
          <div
            className={classNames('df flex-column items-stretch listicle-product__card-container',
              {
                'flex-row-l flex-grow-1': isLarge,
                'flex-row-m': !isLarge,
                'pb6-m': !hasProsCons,
              })}
          >
            { picture() }
            { isLarge && mobileMediaSlide() }
            <div
              className={
                classNames(
                  `${block}__productCardInfo pt5 flex-grow-1`,
                  { pb5: !hasProsCons },
                  { 'pt0-m pb0-m': !isLarge },
                  'listicle-product__card-info',
                )
              }
            >
              { hasPromotionalMedia && isMobile && (
                <PromotionalMedia
                  additionalStyles={{
                    container: 'dn-m df mb7',
                  }}
                  isMobile={isMobile}
                  media={promotionalMedia}
                  selectedIdx={currSlideIdx}
                  selectMedia={changeCurrentMedia}
                />
              )}

              { renderUniCheckoutSeller(offers) }

              <a
                className={`${block}__productTitleLink`}
                data-aps-asc-tag={associateTag}
                data-aps-asin={productAsin}
                data-product-id={productId}
                data-testid="listicle-product-title-link"
                href={productLink}
                onClick={() => {
                  trackEcommerceShopClick({ vertical, product });
                }}
                rel="sponsored noopener noreferrer"
                target="_blank"
              >
                <h2 className={classNames('listicle-product__headline', {
                  'listicle-product__headline--no-description': !primaryDescription,
                })}
                >
                  {name}
                </h2>
                { primaryDescription && (
                  <p
                    className={classNames('listicle-product__description', {
                      'listicle-product__description--large': isLarge,
                    })}
                    data-test="listicle__description"
                    data-testid="listicle__description"
                  >
                    {primaryDescription}
                  </p>
                )}
              </a>
              { renderOffers() }
            </div>
          </div>
        </div>
        {hasProsCons && (
          <ProsCons
            cons={cons}
            dataActivityMapId={getDataActivityMapID(
              {
                componentName: 'listicle-product',
                pageRegion: 'article-body',
                componentTitle: name,
              },
            )}
            isOde={isLarge}
            pros={pros}
          />
        )}
      </Container>
    </div>
  );
};

export default ListicleProduct;

/**
 * @param {object} props - Component props
 * @param {boolean} shouldShowComponent - Should show component
 * @param {string} defaultId - Default ID
 */
export const getIdForLazyListicleProduct = (props, shouldShowComponent, defaultId) => {
  const {
    product: { id },
  } = props;
  return shouldShowComponent ? defaultId || id : undefined;
};


export const LazyListicleProduct = withViewportVisibility(
  ListicleProduct,
  undefined,
  getIdForLazyListicleProduct,
);
